import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Download, FilePlus2, Pencil, Skull, Upload, Trash2 } from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { openProtectedFile } from '../lib/protectedFiles';
import CameraFileInput from './CameraFileInput';
import ProtectedMediaThumbnail from './ProtectedMediaThumbnail';
import { appNowTime, appTodayDate, createClientRequestId } from '../lib/appDateTime';
import NecropsyDiagnosisFields from './NecropsyDiagnosisFields';
import NecropsyExamCards from './NecropsyExamCards';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_OPTIONS = [
  { value: 'necropsia_iniciada', label: 'Necropsia iniciada' },
  { value: 'necropsia_concluida', label: 'Necropsia concluída' },
  { value: 'laudo_externo_pendente', label: 'Laudo externo pendente' },
  { value: 'finalizada', label: 'Finalizada' }
];

const ATTACHMENT_TYPE_OPTIONS = [
  { value: 'foto_geral', label: 'Foto geral' },
  { value: 'lesao_especifica', label: 'Lesão específica' },
  { value: 'orgao', label: 'Órgão' },
  { value: 'lamina', label: 'Lâmina' },
  { value: 'laudo_externo', label: 'Laudo externo' },
  { value: 'documento', label: 'Documento' }
];

const FALLBACK_SAMPLE_TYPES = ['Histopatologia', 'Microbiologia', 'Toxicologia', 'PCR - painel', 'Parasitologia', 'Outros'];
const FALLBACK_STORAGE_OPTIONS = ['Ultrafreezer', 'Freezer comum', 'Geladeira', 'Temperatura ambiente', 'Outros'];
const FALLBACK_PRESERVATION_MODES = ['Alcool 70%', 'Papel aluminio', 'Ependorfe', 'Tubos de coleta de sangue', 'Plástico', 'Formol 10%', 'Alcool 100%', 'A seco', 'Outros'];
const DEATH_OPTIONS = ['Natural', 'Eutanásia'];
const SEX_OPTIONS = ['Fêmea', 'Indefinido', 'Macho'];
const animalSexSnapshot = (animal) => ({ femea: 'Fêmea', female: 'Fêmea', indefinido: 'Indefinido', unknown: 'Indefinido', macho: 'Macho', male: 'Macho' })[String(animal?.sex || animal?.sexo || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()] || '';
const STAGE_OPTIONS = ['Feto', 'Filhote', 'Juvenil', 'Subadulto', 'Adulto', 'Indeterminado'];
const PRESERVATION_OPTIONS = ['Refrigerado', 'Congelado', 'Temperatura ambiente', 'Imediata'];
const photoLegendFallback = ['Exame externo', 'Vista ventral', 'Vista dorsal', 'Vista lateral direita', 'Vista lateral esquerda', 'Pesagem', 'Trato gastrointestinal', 'Resíduos antropogênicos', 'Resíduo oleoso', 'Marca de interação antrópica com rede de pesca', 'Fibropapilomas'];

const mergeNecropsyCatalogs = (catalogs = {}) => ({
  systems: Array.isArray(catalogs.systems) ? catalogs.systems : [],
  organs: Array.isArray(catalogs.organs) ? catalogs.organs : [],
  sample_types: catalogs.sample_types?.length ? catalogs.sample_types : FALLBACK_SAMPLE_TYPES,
  storage_options: catalogs.storage_options?.length ? catalogs.storage_options : FALLBACK_STORAGE_OPTIONS,
  preservation_modes: catalogs.preservation_modes?.length ? catalogs.preservation_modes : FALLBACK_PRESERVATION_MODES,
  photo_legends: Array.isArray(catalogs.photo_legends) ? catalogs.photo_legends : [],
  suspicion_systems: catalogs.suspicion_systems || [],
  suspicion_causes: catalogs.suspicion_causes || [],
  diagnosis_causes: catalogs.diagnosis_causes || [],
  indeterminate_causes: catalogs.indeterminate_causes || [],
  interaction_types: catalogs.interaction_types || [],
  sample_organs: catalogs.sample_organs || [],
  exam_config: catalogs.exam_config || {}
});

const emptySample = () => ({
  id: `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  system_id: '',
  system_name: '',
  organ_id: '',
  tipo_amostra: '',
  tipo_amostra_outros: '',
  orgao_material: '',
  meio_conservacao: '',
  armazenamento: '',
  armazenamento_outros: '',
  modo_preservacao: '',
  modo_preservacao_outros: '',
  destino_setor_analise: '',
  laboratorio_destino: '',
  observacoes: ''
});

const emptyFinding = () => ({
  id: `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  system_id: '',
  system_name: '',
  organ_id: '',
  organ_name: '',
  description: ''
});

const emptyForm = (animal, outcome, user) => ({
  data_obito: '',
  hora_obito: '',
  data_necropsia: appTodayDate(),
  hora_necropsia: appNowTime(),
  local_necropsia: animal?.base_name || '',
  responsavel_tecnico: user?.full_name || user?.name || '',
  participantes_apoio: '',
  observacoes_gerais: '',
  exame_externo: '', suspeita_tipo: '', suspeita_valor: '', suspeita_causa_outros: '',
  condicao_carcaca: null, condicao_morte: outcome?.outcome_type === 'eutanasia' ? 'Eutanásia' : 'Natural',
  sexo: animalSexSnapshot(animal),
  estagio_desenvolvimento: '', metodo_preservacao_carcaca: '', peso_kg: '',
  achados_observacoes: '', interacao_antropica: '', tipos_interacao_antropica: [], grau_evidencia_interacao: null,
  coleta_conteudo_gastrointestinal: null, triagem_detalhada_gastrointestinal: null, descricao_triagem_gastrointestinal: '',
  orgaos_amostras_selecionados: [], orgaos_amostras_outros: [], requested_exam_types: [],
  historico_clinico_resumido: '',
  achados_macroscopicos: '',
  macroscopic_findings: [],
  suspeita_diagnostica: '',
  diagnostico_presuntivo: '',
  diagnostico_final: '',
  diagnostico_presuntivo_estruturado: {},
  diagnostico_final_estruturado: {},
  necropsy_exam_requests: [],
  observacoes_finais: '',
  projeto_vinculado: '',
  pendencias_projeto: '',
  status: 'necropsia_iniciada',
  samples: []
});

const mapNecropsyToForm = (necropsy) => ({
  data_obito: necropsy?.data_obito || '',
  hora_obito: necropsy?.hora_obito || '',
  data_necropsia: necropsy?.data_necropsia || '',
  hora_necropsia: necropsy?.hora_necropsia || '',
  local_necropsia: necropsy?.local_necropsia || '',
  responsavel_tecnico: necropsy?.responsavel_tecnico || '',
  participantes_apoio: necropsy?.participantes_apoio || '',
  observacoes_gerais: necropsy?.observacoes_gerais || '',
  exame_externo: necropsy?.exame_externo || '', suspeita_tipo: necropsy?.suspeita_tipo || '',
  suspeita_valor: necropsy?.suspeita_valor || '', suspeita_causa_outros: necropsy?.suspeita_causa_outros || '',
  condicao_carcaca: necropsy?.condicao_carcaca ?? null, condicao_morte: necropsy?.condicao_morte || '',
  sexo: necropsy?.sexo || '', estagio_desenvolvimento: necropsy?.estagio_desenvolvimento || '',
  metodo_preservacao_carcaca: necropsy?.metodo_preservacao_carcaca || '', peso_kg: necropsy?.peso_kg ?? '',
  achados_observacoes: necropsy?.achados_observacoes || '', interacao_antropica: necropsy?.interacao_antropica || '',
  tipos_interacao_antropica: necropsy?.tipos_interacao_antropica || [], grau_evidencia_interacao: necropsy?.grau_evidencia_interacao ?? null,
  coleta_conteudo_gastrointestinal: necropsy?.coleta_conteudo_gastrointestinal ?? null,
  triagem_detalhada_gastrointestinal: necropsy?.triagem_detalhada_gastrointestinal ?? null,
  descricao_triagem_gastrointestinal: necropsy?.descricao_triagem_gastrointestinal || '',
  orgaos_amostras_selecionados: necropsy?.orgaos_amostras_selecionados || [], orgaos_amostras_outros: necropsy?.orgaos_amostras_outros || [],
  requested_exam_types: necropsy?.requested_exam_types || [],
  historico_clinico_resumido: necropsy?.historico_clinico_resumido || '',
  achados_macroscopicos: necropsy?.achados_macroscopicos || '',
  macroscopic_findings: Array.isArray(necropsy?.macroscopic_findings) ? necropsy.macroscopic_findings : [],
  suspeita_diagnostica: necropsy?.suspeita_diagnostica || '',
  diagnostico_presuntivo: necropsy?.diagnostico_presuntivo || '',
  diagnostico_final: necropsy?.diagnostico_final || '',
  diagnostico_presuntivo_estruturado: necropsy?.diagnostico_presuntivo_estruturado || {},
  diagnostico_final_estruturado: necropsy?.diagnostico_final_estruturado || {},
  necropsy_exam_requests: (necropsy?.necropsy_exam_requests?.length ? necropsy.necropsy_exam_requests : (necropsy?.requested_exam_types || []).map((exam_type) => ({ id: `legacy-${exam_type.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`, exam_type, organ_samples: [], organ_samples_other: [] }))),
  observacoes_finais: necropsy?.observacoes_finais || '',
  projeto_vinculado: necropsy?.projeto_vinculado || '',
  pendencias_projeto: necropsy?.pendencias_projeto || '',
  status: necropsy?.status || 'necropsia_iniciada',
  samples: Array.isArray(necropsy?.samples)
    ? necropsy.samples.map((sample) => ({
      ...sample,
      modo_preservacao: sample.modo_preservacao || sample.meio_conservacao || '',
    }))
    : []
});

const nullableText = (value) => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const necropsyErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => {
      if (typeof item === 'string') return item;
      if (!item || typeof item !== 'object') return '';
      const field = Array.isArray(item.loc) ? item.loc.filter((part) => part !== 'body').join('.') : '';
      return [field, item.msg].filter(Boolean).join(': ');
    }).filter(Boolean);
    if (messages.length) return messages.join('; ');
  }
  return fallback;
};

export default function NecropsyTab({ animalId, animal, user, onNecropsyChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [necropsy, setNecropsy] = useState(null);
  const [organCatalog, setOrganCatalog] = useState([]);
  const [necropsyCatalogs, setNecropsyCatalogs] = useState({
    systems: [],
    organs: [],
    sample_types: FALLBACK_SAMPLE_TYPES,
    storage_options: FALLBACK_STORAGE_OPTIONS,
    preservation_modes: FALLBACK_PRESERVATION_MODES,
    photo_legends: []
  });
  const [form, setForm] = useState(emptyForm(animal, null, user));
  const [showForm, setShowForm] = useState(false);
  const [necropsyClientRequestId, setNecropsyClientRequestId] = useState(createClientRequestId('necropsy'));
  const [attachmentType, setAttachmentType] = useState('foto_geral');
  const [attachmentOrganId, setAttachmentOrganId] = useState('');
  const [attachmentOrganName, setAttachmentOrganName] = useState('');
  const [attachmentCaption, setAttachmentCaption] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [uploadCount, setUploadCount] = useState(0);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [otherSampleName, setOtherSampleName] = useState('');
  const [attachmentObservation, setAttachmentObservation] = useState('');
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [attachmentEditForm, setAttachmentEditForm] = useState({ legenda: '', orgao_id: '', orgao_nome: '', observacao: '' });
  const [conclusionStatus, setConclusionStatus] = useState('finalizada');

  const canView = hasPermission(user, 'necropsy.view');
  const canEdit = hasPermission(user, 'necropsy.update');
  const canCreate = hasPermission(user, 'necropsy.create');
  const canAttach = hasPermission(user, 'necropsy.attach');
  const canConclude = hasPermission(user, 'necropsy.conclude');
  const canGeneratePdf = hasPermission(user, 'pdfs.generate') && canView;
  const canDelete = hasPermission(user, 'records.delete');
  const isPrivilegedPhotoManager = ['administrador', 'programador'].includes(user?.role);

  const canManageAttachment = (attachment) => {
    if (isPrivilegedPhotoManager) return true;
    if (user?.role !== 'veterinario') return false;
    if (attachment?.uploaded_by !== user?.id) return false;
    const uploadedAt = new Date(attachment?.uploaded_at);
    if (Number.isNaN(uploadedAt.getTime())) return false;
    return Date.now() - uploadedAt.getTime() <= 4 * 60 * 60 * 1000;
  };

  const handleOpenAttachment = async (event, attachment) => {
    event.preventDefault();
    try {
      await openProtectedFile(attachment.file_url, attachment.legenda || attachment.filename);
    } catch (error) {
      toast.error(error.message || 'Não foi possível abrir o arquivo');
    }
  };

  const compatibleOutcome = useMemo(
    () => outcome && ['obito', 'eutanasia'].includes(outcome.outcome_type),
    [outcome]
  );
  const systems = necropsyCatalogs.systems || [];
  const organs = organCatalog;
  const photoLegends = necropsyCatalogs.photo_legends?.length > 0 ? necropsyCatalogs.photo_legends : photoLegendFallback;

  const organsForSystem = (systemId) => organs.filter((organ) => !systemId || organ.system_id === systemId);

  const findOrgan = (organId) => organs.find((organ) => organ.id === organId);

  const updateAttachmentState = (nextNecropsy) => {
    if (!nextNecropsy) return;
    setNecropsy((current) => ({
      ...(current || nextNecropsy),
      attachments: nextNecropsy.attachments || [],
      updated_at: nextNecropsy.updated_at,
      updated_by: nextNecropsy.updated_by
    }));
  };

  const refreshNecropsyAttachments = async (necropsyId = necropsy?.id) => {
    if (!necropsyId) return;
    const response = await axios.get(`${API}/api/necropsies/${necropsyId}`);
    updateAttachmentState(response.data);
  };

  useEffect(() => {
    if (animalId && canView) {
      fetchData();
    }
  }, [animalId, canView, animal?.status, animal?.outcome_date]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [outcomeResponse, necropsyResponse] = await Promise.allSettled([
        axios.get(`${API}/api/animals/${animalId}/outcome`),
        axios.get(`${API}/api/animals/${animalId}/necropsies`)
      ]);
      const catalogsResponse = await axios.get(`${API}/api/necropsy/catalogs`).catch(async () => {
        const organsResponse = await axios.get(`${API}/api/necropsy/organs`).catch(() => ({ data: [] }));
        return {
          data: {
            systems: [],
            organs: organsResponse.data || [],
            sample_types: FALLBACK_SAMPLE_TYPES,
            storage_options: FALLBACK_STORAGE_OPTIONS,
            preservation_modes: FALLBACK_PRESERVATION_MODES,
            photo_legends: []
          }
        };
      });

      const nextOutcome = outcomeResponse.status === 'fulfilled' ? outcomeResponse.value.data : null;
      const list = necropsyResponse.status === 'fulfilled' ? necropsyResponse.value.data : [];
      const currentNecropsy = Array.isArray(list) && list.length > 0 ? list[0] : null;

      setOutcome(nextOutcome);
      setNecropsy(currentNecropsy);
      const nextCatalogs = mergeNecropsyCatalogs(catalogsResponse.data);
      setNecropsyCatalogs(nextCatalogs);
      setOrganCatalog(nextCatalogs.organs);
      setForm(currentNecropsy ? mapNecropsyToForm(currentNecropsy) : emptyForm(animal, nextOutcome, user));
      setConclusionStatus(currentNecropsy?.status && currentNecropsy.status !== 'necropsia_iniciada' ? currentNecropsy.status : 'finalizada');
      setShowForm(Boolean(currentNecropsy));
    } catch (error) {
      toast.error('Erro ao carregar necropsia');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleArrayField = (field, value) => setForm((current) => ({
    ...current,
    [field]: current[field].includes(value) ? current[field].filter((item) => item !== value) : [...current[field], value]
  }));

  const renderChoices = (field, options) => (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const value = typeof option === 'object' ? option.value : option;
        const label = typeof option === 'object' ? option.label : option;
        return <button key={String(value)} type="button" disabled={!canEdit && Boolean(necropsy)} onClick={() => field === 'suspeita_tipo' ? setForm((current) => ({ ...current, suspeita_tipo: value, suspeita_valor: '', suspeita_causa_outros: '' })) : updateField(field, value)} className={`rounded-lg border px-3 py-2 text-sm ${form[field] === value ? 'border-emerald-600 bg-emerald-50 text-emerald-900' : 'border-gray-300 bg-white'}`}>{label}</button>;
      })}
    </div>
  );

  const renderChecks = (field, options) => (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {options.map((option) => <label key={option} className="flex min-w-0 items-start gap-2 rounded border p-2 text-sm"><input type="checkbox" checked={form[field].includes(option)} disabled={!canEdit && Boolean(necropsy)} onChange={() => toggleArrayField(field, option)} /><span className="break-words">{option}</span></label>)}
    </div>
  );

  const updateSampleField = (index, field, value) => {
    setForm((current) => {
      const nextSamples = [...current.samples];
      nextSamples[index] = { ...nextSamples[index], [field]: value };
      if (field === 'system_id') {
        if (value === '__legacy__') {
          nextSamples[index].system_id = '';
          return { ...current, samples: nextSamples };
        }
        const selectedSystem = systems.find((system) => system.id === value);
        nextSamples[index].system_name = selectedSystem?.name || '';
        nextSamples[index].organ_id = '';
        nextSamples[index].orgao_material = '';
      }
      if (field === 'organ_id') {
        if (value === '__legacy__') {
          nextSamples[index].organ_id = '';
        } else if (value === '__outros__') {
          nextSamples[index].orgao_material = '';
        } else {
          const selected = findOrgan(value);
          if (selected) {
            nextSamples[index].orgao_material = selected.organ_name || selected.name;
            nextSamples[index].system_id = selected.system_id || nextSamples[index].system_id || '';
            nextSamples[index].system_name = selected.system_name || selected.category || nextSamples[index].system_name || '';
          }
        }
      }
      return { ...current, samples: nextSamples };
    });
  };

  const updateFindingField = (index, field, value) => {
    setForm((current) => {
      const nextFindings = [...current.macroscopic_findings];
      nextFindings[index] = { ...nextFindings[index], [field]: value };
      if (field === 'system_id') {
        if (value === '__legacy__') {
          nextFindings[index].system_id = '';
          return { ...current, macroscopic_findings: nextFindings };
        }
        const selectedSystem = systems.find((system) => system.id === value);
        nextFindings[index].system_name = selectedSystem?.name || '';
        nextFindings[index].organ_id = '';
        nextFindings[index].organ_name = '';
      }
      if (field === 'organ_id') {
        if (value === '__legacy__') {
          nextFindings[index].organ_id = '';
          return { ...current, macroscopic_findings: nextFindings };
        }
        const selected = findOrgan(value);
        if (selected) {
          nextFindings[index].organ_name = selected.organ_name || selected.name;
          nextFindings[index].system_id = selected.system_id || nextFindings[index].system_id || '';
          nextFindings[index].system_name = selected.system_name || selected.category || nextFindings[index].system_name || '';
        }
      }
      return { ...current, macroscopic_findings: nextFindings };
    });
  };

  const addFinding = () => {
    setForm((current) => ({ ...current, macroscopic_findings: [...current.macroscopic_findings, emptyFinding()] }));
  };

  const removeFinding = (index) => {
    setForm((current) => ({ ...current, macroscopic_findings: current.macroscopic_findings.filter((_, findingIndex) => findingIndex !== index) }));
  };

  const addSample = () => {
    setForm((current) => ({ ...current, samples: [...current.samples, emptySample()] }));
  };

  const removeSample = (index) => {
    setForm((current) => ({ ...current, samples: current.samples.filter((_, sampleIndex) => sampleIndex !== index) }));
  };

  const buildPayload = () => ({
    ...form,
    peso_kg: form.peso_kg === '' ? null : Number(form.peso_kg),
    suspeita_valor: form.suspeita_tipo ? nullableText(form.suspeita_valor) : null,
    suspeita_causa_outros: form.suspeita_tipo === 'causa' && form.suspeita_valor === 'Outros' ? nullableText(form.suspeita_causa_outros) : null,
    tipos_interacao_antropica: form.interacao_antropica === 'Sim' ? form.tipos_interacao_antropica : [],
    grau_evidencia_interacao: form.interacao_antropica === 'Sim' ? form.grau_evidencia_interacao : null,
    descricao_triagem_gastrointestinal: form.triagem_detalhada_gastrointestinal ? nullableText(form.descricao_triagem_gastrointestinal) : null,
    orgaos_amostras_outros: form.orgaos_amostras_selecionados.includes('Outros') ? form.orgaos_amostras_outros : [],
    data_obito: nullableText(form.data_obito),
    hora_obito: nullableText(form.hora_obito),
    data_necropsia: nullableText(form.data_necropsia),
    hora_necropsia: nullableText(form.hora_necropsia),
    client_request_id: necropsy?.id ? undefined : necropsyClientRequestId,
    macroscopic_findings: form.macroscopic_findings
      .filter((finding) => finding.organ_name || finding.description)
      .map((finding) => ({
        ...finding,
        system_id: finding.system_id === '__legacy__' ? null : nullableText(finding.system_id),
        system_name: nullableText(finding.system_name),
        organ_id: finding.organ_id === '__legacy__' ? null : nullableText(finding.organ_id),
        organ_name: nullableText(finding.organ_name) || 'Não informado',
        description: nullableText(finding.description) || ''
      })),
    samples: form.samples
      .filter((sample) => sample.tipo_amostra || sample.orgao_material || sample.meio_conservacao || sample.armazenamento || sample.modo_preservacao)
      .map((sample) => ({
        ...sample,
        system_id: sample.system_id === '__legacy__' ? null : nullableText(sample.system_id),
        system_name: nullableText(sample.system_name),
        organ_id: ['__outros__', '__legacy__'].includes(sample.organ_id) ? null : nullableText(sample.organ_id),
        tipo_amostra: nullableText(sample.tipo_amostra) || 'Outros',
        orgao_material: nullableText(sample.orgao_material) || 'Não informado',
        meio_conservacao: nullableText(sample.meio_conservacao || sample.modo_preservacao),
        armazenamento: nullableText(sample.armazenamento),
        modo_preservacao: nullableText(sample.modo_preservacao),
        tipo_amostra_outros: nullableText(sample.tipo_amostra_outros),
        armazenamento_outros: nullableText(sample.armazenamento_outros),
        modo_preservacao_outros: nullableText(sample.modo_preservacao_outros),
      }))
  });

  const handleSave = async () => {
    if (saving) return;
    if (!form.responsavel_tecnico?.trim()) {
      toast.error('Informe o responsável técnico');
      return;
    }
    if (!compatibleOutcome) {
      toast.error('Necropsia só pode ser registrada para óbito ou eutanásia');
      return;
    }
    if (form.condicao_morte && form.condicao_morte !== (outcome.outcome_type === 'eutanasia' ? 'Eutanásia' : 'Natural')) {
      toast.error('Condição da morte incompatível com o desfecho registrado');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      let savedNecropsy;
      if (necropsy?.id) {
        const response = await axios.put(`${API}/api/necropsies/${necropsy.id}`, payload);
        savedNecropsy = response.data;
      } else {
        const response = await axios.post(`${API}/api/animals/${animalId}/necropsies`, payload);
        savedNecropsy = response.data;
        setNecropsyClientRequestId(createClientRequestId('necropsy'));
      }
      setNecropsy(savedNecropsy);
      setForm(mapNecropsyToForm(savedNecropsy));
      setShowForm(true);
      toast.success('Necropsia salva com sucesso');
      let failedAttachments = 0;
      if (pendingAttachments.length > 0) {
        const uploadResult = await uploadAttachmentItems(pendingAttachments, savedNecropsy.id);
        failedAttachments = uploadResult.failed;
      }
      if (failedAttachments === 0) onNecropsyChange?.();
    } catch (error) {
      toast.error(necropsyErrorMessage(error, 'Erro ao salvar necropsia'));
    } finally {
      setSaving(false);
    }
  };

  const handleConclude = async () => {
    if (!necropsy?.id) {
      toast.error('Salve a necropsia antes de concluir');
      return;
    }
    try {
      const response = await axios.post(`${API}/api/necropsies/${necropsy.id}/conclude`, {
        status: conclusionStatus
      });
      setNecropsy(response.data);
      setForm(mapNecropsyToForm(response.data));
      toast.success('Necropsia concluída');
      onNecropsyChange?.();
    } catch (error) {
      toast.error(necropsyErrorMessage(error, 'Erro ao concluir necropsia'));
    }
  };

  const uploadAttachmentItems = async (items, necropsyId) => {
    if (!items.length || !necropsyId) return { failed: items.length };
    setUploading(true);
    setUploadCount(items.length);
    let sent = 0;
    let processed = 0;
    let firstError;
    try {
      for (const item of items) {
        const formData = new FormData();
        formData.append('file', item.file);
        formData.append('tipo', item.tipo);
        if (item.legenda) formData.append('legenda', item.legenda);
        if (item.observacao) formData.append('observacao', item.observacao);
        if (item.orgao_id) formData.append('orgao_id', item.orgao_id);
        if (item.orgao_nome) formData.append('orgao_nome', item.orgao_nome);
        try {
          await axios.post(`${API}/api/necropsies/${necropsyId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          sent += 1;
          setPendingAttachments((current) => current.filter((pending) => pending.id !== item.id));
        } catch (error) {
          firstError ||= error;
        }
        processed += 1;
        setUploadCount(items.length - processed);
      }
      if (sent > 0) toast.success(`${sent} anexo(s) enviado(s)`);
      if (firstError) {
        const detail = firstError.response?.data?.detail;
        toast.error(`${items.length - sent} anexo(s) pendente(s): ${typeof detail === 'string' ? detail : 'erro no envio'}`);
      }
    } finally {
      if (sent > 0) {
        try {
          await refreshNecropsyAttachments(necropsyId);
        } catch (error) {
          toast.error('Fotos enviadas, mas não foi possível atualizar a lista');
        }
      }
      setUploading(false);
      setUploadCount(0);
    }
    return { failed: items.length - sent };
  };

  const handleAttachmentUpload = async (files) => {
    if (!files?.length) return;
    if (attachmentCaption === '__new__') { toast.error('Cadastre a nova legenda antes de selecionar os arquivos'); return; }
    const items = files.map((file) => ({
      id: createClientRequestId('necropsy-attachment'), file,
      tipo: attachmentType, legenda: attachmentCaption, observacao: attachmentObservation,
      orgao_id: attachmentOrganId, orgao_nome: attachmentOrganName
    }));
    setPendingAttachments((current) => [...current, ...items]);
    setAttachmentCaption('');
    setAttachmentObservation('');
    setAttachmentOrganId('');
    setAttachmentOrganName('');
    if (necropsy?.id) {
      await uploadAttachmentItems(items, necropsy.id);
    } else {
      toast.success(`${items.length} anexo(s) selecionado(s) para envio ao salvar`);
    }
  };

  const savePhotoLegend = async () => {
    try {
      const response = await axios.post(`${API}/api/necropsy/photo-legends`, { name: newCaption });
      const name = response.data.name;
      setNecropsyCatalogs((current) => ({ ...current, photo_legends: [...current.photo_legends, name] }));
      setAttachmentCaption(name);
      setNewCaption('');
      toast.success('Legenda cadastrada');
    } catch (error) {
      toast.error(necropsyErrorMessage(error, 'Erro ao cadastrar legenda'));
    }
  };

  const handleEditAttachment = async (attachment) => {
    if (!attachment?.file_url || !necropsy?.id) return;
    setEditingAttachment(attachment);
    setAttachmentEditForm({
      legenda: attachment.legenda || '',
      orgao_id: attachment.orgao_id || '',
      orgao_nome: attachment.orgao_nome || '',
      observacao: attachment.observacao || ''
    });
  };

  const handleSaveAttachmentEdit = async () => {
    if (!editingAttachment?.file_url || !necropsy?.id) return;
    try {
      await axios.put(`${API}/api/necropsies/${necropsy.id}/attachments`, {
        file_url: editingAttachment.file_url,
        legenda: attachmentEditForm.legenda,
        observacao: attachmentEditForm.observacao,
        orgao_id: attachmentEditForm.orgao_id,
        orgao_nome: attachmentEditForm.orgao_nome
      });
      toast.success('Anexo atualizado');
      setEditingAttachment(null);
      await refreshNecropsyAttachments();
    } catch (error) {
      toast.error(necropsyErrorMessage(error, 'Erro ao atualizar anexo'));
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!attachment?.file_url || !necropsy?.id) return;
    if (!window.confirm('Excluir este anexo da necropsia?')) return;
    try {
      await axios.delete(`${API}/api/necropsies/${necropsy.id}/attachments`, {
        params: { file_url: attachment.file_url }
      });
      toast.success('Anexo excluído');
      await refreshNecropsyAttachments();
    } catch (error) {
      toast.error(necropsyErrorMessage(error, 'Erro ao excluir anexo'));
    }
  };

  const handleDownloadPdf = async () => {
    if (!necropsy?.id) return;
    try {
      const response = await axios.get(`${API}/api/necropsies/${necropsy.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `necropsia_${animal?.ibama_id || animalId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF da necropsia baixado');
    } catch (error) {
      toast.error(necropsyErrorMessage(error, 'Erro ao gerar PDF'));
    }
  };

  const handleDeleteNecropsy = async () => {
    if (!necropsy?.id) return;
    if (!window.confirm('Excluir esta necropsia? A ação ficará registrada na auditoria.')) return;
    try {
      await axios.delete(`${API}/api/necropsies/${necropsy.id}`);
      toast.success('Necropsia excluída');
      setNecropsy(null);
      setPendingAttachments([]);
      setShowForm(false);
      fetchData();
      onNecropsyChange?.();
    } catch (error) {
      toast.error(necropsyErrorMessage(error, 'Erro ao excluir necropsia'));
    }
  };

  if (!canView) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-gray-500">
          Você não possui permissão para visualizar a necropsia.
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!compatibleOutcome) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-900">
            <Skull className="h-5 w-5" />
            Necropsia indisponível
          </CardTitle>
          <CardDescription className="text-amber-800">
            A necropsia só pode ser usada para animais com desfecho em óbito ou eutanásia.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-orange-900">Necropsia</CardTitle>
            <CardDescription className="text-orange-800">
              Registro técnico vinculado ao desfecho {outcome?.outcome_type === 'eutanasia' ? 'eutanásia' : 'óbito'}.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {necropsy?.status && <Badge className="bg-orange-100 text-orange-900">{necropsy.status.replaceAll('_', ' ')}</Badge>}
            {!necropsy && canCreate && (
              <Button onClick={() => setShowForm(true)} className="bg-orange-600 hover:bg-orange-700">
                <FilePlus2 className="mr-2 h-4 w-4" />
                Registrar Necropsia
              </Button>
            )}
            {necropsy && canGeneratePdf && (
              <Button variant="outline" onClick={handleDownloadPdf}>
                <Download className="mr-2 h-4 w-4" />
                PDF
              </Button>
            )}
            {necropsy && canDelete && (
              <Button variant="outline" onClick={handleDeleteNecropsy} className="border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir Necropsia
              </Button>
            )}
          </div>
        </CardHeader>
        {necropsy && (
          <CardContent className="grid gap-3 text-sm md:grid-cols-2">
            <div><strong>Responsável técnico:</strong> {necropsy.responsavel_tecnico || 'N/A'}</div>
            <div><strong>Data da necropsia:</strong> {necropsy.data_necropsia || 'N/A'} {necropsy.hora_necropsia || ''}</div>
            <div><strong>Projeto:</strong> {necropsy.projeto_vinculado || 'N/A'}</div>
            <div><strong>Amostras:</strong> {necropsy.samples?.length || 0}</div>
            <div className="md:col-span-2"><strong>Diagnóstico final:</strong> {necropsy.diagnostico_final || 'Não informado'}</div>
          </CardContent>
        )}
      </Card>

      {(showForm || necropsy) && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Dados Gerais</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Data do óbito</Label>
                <Input type="date" value={form.data_obito} onChange={(e) => updateField('data_obito', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
              </div>
              <div>
                <Label>Hora do óbito</Label>
                <Input type="time" value={form.hora_obito} onChange={(e) => updateField('hora_obito', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
              </div>
              <div>
                <Label>Data da necropsia</Label>
                <Input type="date" value={form.data_necropsia} onChange={(e) => updateField('data_necropsia', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
              </div>
              <div>
                <Label>Hora da necropsia</Label>
                <Input type="time" value={form.hora_necropsia} onChange={(e) => updateField('hora_necropsia', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
              </div>
              <div>
                <Label>Local da necropsia</Label>
                <Input value={form.local_necropsia} onChange={(e) => updateField('local_necropsia', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
              </div>
              <div>
                <Label>Responsável técnico</Label>
                <Input value={form.responsavel_tecnico} onChange={(e) => updateField('responsavel_tecnico', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
              </div>
              <div className="md:col-span-2">
                <Label>Participantes / apoio</Label>
                <Input value={form.participantes_apoio} onChange={(e) => updateField('participantes_apoio', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
              </div>
              <div className="md:col-span-2">
                <Label>Exame externo</Label>
                <Textarea value={form.exame_externo} onChange={(e) => updateField('exame_externo', e.target.value)} rows={3} disabled={!canEdit && Boolean(necropsy)} />
              </div>
              {form.observacoes_gerais && <p className="text-sm text-gray-600 md:col-span-2"><strong>Informação legada — Observações gerais:</strong> {form.observacoes_gerais}</p>}
              <div className="md:col-span-2"><Label>Histórico clínico resumido</Label><Textarea value={form.historico_clinico_resumido} onChange={(e) => updateField('historico_clinico_resumido', e.target.value)} rows={4} disabled={!canEdit && Boolean(necropsy)} /></div>
              <div className="md:col-span-2 space-y-3"><Label>SUSPEITA</Label>{renderChoices('suspeita_tipo', [{ value: 'sistema', label: 'SISTEMA' }, { value: 'causa', label: 'CAUSA' }])}
                {form.suspeita_tipo && <Select value={form.suspeita_valor || ''} onValueChange={(value) => updateField('suspeita_valor', value)} disabled={!canEdit && Boolean(necropsy)}><SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger><SelectContent>{(form.suspeita_tipo === 'sistema' ? necropsyCatalogs.suspicion_systems : necropsyCatalogs.suspicion_causes).map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select>}
                {form.suspeita_tipo === 'causa' && form.suspeita_valor === 'Outros' && <div><Label>Outra causa</Label><Input value={form.suspeita_causa_outros} onChange={(e) => updateField('suspeita_causa_outros', e.target.value)} /></div>}
              </div>
              <div><Label>Condição da carcaça</Label>{renderChoices('condicao_carcaca', [2, 3, 4, 5])}</div>
              <div><Label>Condição da morte</Label>{renderChoices('condicao_morte', DEATH_OPTIONS)}</div>
              <div><Label>Sexo</Label>{renderChoices('sexo', SEX_OPTIONS)}</div>
              <div><Label>Estágio de desenvolvimento</Label>{renderChoices('estagio_desenvolvimento', STAGE_OPTIONS)}</div>
              <div><Label>Método de preservação da carcaça</Label>{renderChoices('metodo_preservacao_carcaca', PRESERVATION_OPTIONS)}</div>
              <div><Label>Peso (kg)</Label><Input type="number" min="0" step="any" value={form.peso_kg} onChange={(e) => updateField('peso_kg', e.target.value)} disabled={!canEdit && Boolean(necropsy)} /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Achados</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {form.achados_macroscopicos && <p className="text-sm text-gray-600"><strong>Informação legada — Achados macroscópicos gerais:</strong> {form.achados_macroscopicos}</p>}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Label>Achados macroscópicos por órgão</Label>
                  {canEdit && (
                    <Button type="button" variant="outline" size="sm" onClick={addFinding}>
                      + Órgão
                    </Button>
                  )}
                </div>
                {form.macroscopic_findings.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum achado por órgão registrado.</p>
                ) : (
                  form.macroscopic_findings.map((finding, index) => (
                    <div key={finding.id || index} className="rounded-lg border border-gray-200 p-3">
                      <div className="grid gap-3 md:grid-cols-[1fr_1fr_2fr_auto]">
                        <div>
                          <Label>Sistema</Label>
                          {systems.length > 0 ? (
                            <Select value={finding.system_id || (finding.system_name ? '__legacy__' : '')} onValueChange={(value) => updateFindingField(index, 'system_id', value)} disabled={!canEdit && Boolean(necropsy)}>
                              <SelectTrigger><SelectValue placeholder="Selecionar sistema" /></SelectTrigger>
                              <SelectContent>
                                {finding.system_name && !finding.system_id && (
                                  <SelectItem value="__legacy__">{finding.system_name}</SelectItem>
                                )}
                                {systems.map((system) => (
                                  <SelectItem key={system.id} value={system.id}>{system.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={finding.system_name || ''} onChange={(e) => updateFindingField(index, 'system_name', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
                          )}
                        </div>
                        <div>
                          <Label>Órgão</Label>
                          {organCatalog.length > 0 ? (
                            <Select value={finding.organ_id || (finding.organ_name ? '__legacy__' : '')} onValueChange={(value) => updateFindingField(index, 'organ_id', value)} disabled={!canEdit && Boolean(necropsy)}>
                              <SelectTrigger><SelectValue placeholder="Selecionar órgão" /></SelectTrigger>
                              <SelectContent>
                                {finding.organ_name && !finding.organ_id && (
                                  <SelectItem value="__legacy__">{finding.organ_name}</SelectItem>
                                )}
                                {organsForSystem(finding.system_id).map((organ) => (
                                  <SelectItem key={organ.id} value={organ.id}>{organ.organ_name || organ.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={finding.organ_name || ''} onChange={(e) => updateFindingField(index, 'organ_name', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
                          )}
                        </div>
                        <div>
                          <Label>Descrição do achado</Label>
                          <Textarea value={finding.description || ''} onChange={(e) => updateFindingField(index, 'description', e.target.value)} rows={2} disabled={!canEdit && Boolean(necropsy)} />
                        </div>
                        {canEdit && (
                          <div className="flex items-end">
                            <Button type="button" variant="ghost" className="text-red-600" onClick={() => removeFinding(index)}>
                              Remover
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div><Label>Observações</Label><Textarea value={form.achados_observacoes} onChange={(e) => updateField('achados_observacoes', e.target.value)} rows={3} disabled={!canEdit && Boolean(necropsy)} /></div>
              <div className="space-y-2"><Label>Há indícios de interação antrópica?</Label>{renderChoices('interacao_antropica', ['Sim', 'Não', 'Indeterminado'])}</div>
              {form.interacao_antropica === 'Sim' && <>
                <div className="space-y-2"><Label>Tipos de interação antrópica</Label>{renderChecks('tipos_interacao_antropica', necropsyCatalogs.interaction_types)}</div>
                <div className="space-y-2"><Label>Grau de evidência</Label>{renderChoices('grau_evidencia_interacao', [{ value: 1, label: '1 - Fraco' }, { value: 2, label: '2 - Médio' }, { value: 3, label: '3 - Forte' }])}</div>
              </>}
              <div className="space-y-2"><Label>Coleta conteúdo gastrointestinal</Label>{renderChoices('coleta_conteudo_gastrointestinal', [{ value: true, label: 'Sim' }, { value: false, label: 'Não' }])}</div>
              <div className="space-y-2"><Label>Triagem detalhada do conteúdo gastrointestinal</Label>{renderChoices('triagem_detalhada_gastrointestinal', [{ value: true, label: 'Sim' }, { value: false, label: 'Não' }])}</div>
              {form.triagem_detalhada_gastrointestinal === true && <div><Label>Descrição da triagem</Label><Textarea value={form.descricao_triagem_gastrointestinal} onChange={(e) => updateField('descricao_triagem_gastrointestinal', e.target.value)} /></div>}
            </CardContent>
          </Card>

          <Card><CardHeader><CardTitle>3. Diagnóstico</CardTitle></CardHeader><CardContent className="space-y-4">
            {form.suspeita_diagnostica && <p className="text-sm text-gray-600"><strong>Informação legada — Suspeita diagnóstica:</strong> {form.suspeita_diagnostica}</p>}
            <div><Label>Diagnóstico presuntivo</Label><Textarea value={form.diagnostico_presuntivo} onChange={(e) => updateField('diagnostico_presuntivo', e.target.value)} rows={3} disabled={!canEdit && Boolean(necropsy)} /></div>
            <NecropsyDiagnosisFields value={form.diagnostico_presuntivo_estruturado} systems={necropsyCatalogs.suspicion_systems} causes={necropsyCatalogs.diagnosis_causes} indeterminateCauses={necropsyCatalogs.indeterminate_causes} disabled={!canEdit && Boolean(necropsy)} onChange={(value) => updateField('diagnostico_presuntivo_estruturado', value)} />
            <div><Label>Diagnóstico final</Label><Textarea value={form.diagnostico_final} onChange={(e) => updateField('diagnostico_final', e.target.value)} rows={3} disabled={!canEdit && Boolean(necropsy)} /></div>
            <NecropsyDiagnosisFields value={form.diagnostico_final_estruturado} systems={necropsyCatalogs.suspicion_systems} causes={necropsyCatalogs.diagnosis_causes} indeterminateCauses={necropsyCatalogs.indeterminate_causes} final disabled={!canEdit && Boolean(necropsy)} onChange={(value) => updateField('diagnostico_final_estruturado', value)} />
          </CardContent></Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>4. Amostras / Coletas</CardTitle>
                <CardDescription>Cada exame solicitado possui sua própria coleta.</CardDescription>
              </div>
              {canEdit && form.samples.length > 0 && (
                <Button type="button" variant="outline" onClick={addSample}>
                  + Amostra
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {(form.orgaos_amostras_selecionados.length > 0 || form.samples.length > 0) && <div className="rounded border p-3"><p className="mb-2 text-sm font-medium">Coletas legadas</p><div className="space-y-2"><Label>ÓRGÃO / AMOSTRA</Label>{renderChecks('orgaos_amostras_selecionados', necropsyCatalogs.sample_organs)}</div>
              {form.orgaos_amostras_selecionados.includes('Outros') && <div className="space-y-2"><Label>Outro Órgão/Amostra</Label><div className="flex flex-wrap gap-2"><Input className="min-w-0 flex-1" value={otherSampleName} onChange={(e) => setOtherSampleName(e.target.value)} /><Button type="button" variant="outline" onClick={() => { const name = otherSampleName.trim(); if (name && !form.orgaos_amostras_outros.some((item) => item.toLocaleLowerCase() === name.toLocaleLowerCase())) { updateField('orgaos_amostras_outros', [...form.orgaos_amostras_outros, name]); setOtherSampleName(''); } }}>Adicionar</Button></div><div className="flex flex-wrap gap-2">{form.orgaos_amostras_outros.map((name) => <button type="button" key={name} className="rounded border px-2 py-1 text-sm" onClick={() => updateField('orgaos_amostras_outros', form.orgaos_amostras_outros.filter((item) => item !== name))}>{name} ×</button>)}</div></div>}</div>}
              <NecropsyExamCards requests={form.necropsy_exam_requests} config={necropsyCatalogs.exam_config} disabled={!canEdit && Boolean(necropsy)} onChange={(requests) => setForm((current) => ({ ...current, necropsy_exam_requests: requests, requested_exam_types: [...new Set(requests.map((item) => item.exam_type).filter(Boolean))] }))} />
              {form.samples.length === 0 && (
                <p className="text-sm text-gray-500">Nenhuma amostra cadastrada.</p>
              )}
              {form.samples.map((sample, index) => (
                <div key={sample.id || index} className="rounded-lg border border-gray-200 p-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Sistema</Label>
                      <Select value={sample.system_id || (sample.system_name ? '__legacy__' : '')} onValueChange={(value) => updateSampleField(index, 'system_id', value)} disabled={!canEdit && Boolean(necropsy)}>
                        <SelectTrigger><SelectValue placeholder="Selecionar sistema" /></SelectTrigger>
                        <SelectContent>
                          {sample.system_name && !sample.system_id && (
                            <SelectItem value="__legacy__">{sample.system_name}</SelectItem>
                          )}
                          {systems.map((system) => (
                            <SelectItem key={system.id} value={system.id}>{system.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Órgão / material</Label>
                      <Select value={sample.organ_id || (sample.orgao_material ? '__outros__' : '')} onValueChange={(value) => updateSampleField(index, 'organ_id', value)} disabled={!canEdit && Boolean(necropsy)}>
                        <SelectTrigger><SelectValue placeholder="Selecionar órgão/material" /></SelectTrigger>
                        <SelectContent>
                          {organsForSystem(sample.system_id).map((organ) => (
                            <SelectItem key={organ.id} value={organ.id}>{organ.organ_name || organ.name}</SelectItem>
                          ))}
                          <SelectItem value="__outros__">Outros</SelectItem>
                        </SelectContent>
                      </Select>
                      {(sample.organ_id === '__outros__' || (!sample.organ_id && sample.orgao_material)) && (
                        <Input className="mt-2" value={sample.orgao_material || ''} onChange={(e) => updateSampleField(index, 'orgao_material', e.target.value)} placeholder="Informar material" disabled={!canEdit && Boolean(necropsy)} />
                      )}
                    </div>
                    <div>
                      <Label>Tipo de amostra</Label>
                      <Select value={sample.tipo_amostra || ''} onValueChange={(value) => updateSampleField(index, 'tipo_amostra', value)} disabled={!canEdit && Boolean(necropsy)}>
                        <SelectTrigger><SelectValue placeholder="Selecionar tipo" /></SelectTrigger>
                        <SelectContent>
                          {sample.tipo_amostra && !(necropsyCatalogs.sample_types || FALLBACK_SAMPLE_TYPES).includes(sample.tipo_amostra) && (
                            <SelectItem value={sample.tipo_amostra}>{sample.tipo_amostra}</SelectItem>
                          )}
                          {(necropsyCatalogs.sample_types || FALLBACK_SAMPLE_TYPES).map((option) => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {sample.tipo_amostra === 'Outros' && (
                        <Input className="mt-2" value={sample.tipo_amostra_outros || ''} onChange={(e) => updateSampleField(index, 'tipo_amostra_outros', e.target.value)} placeholder="Especificar tipo" disabled={!canEdit && Boolean(necropsy)} />
                      )}
                    </div>
                    <div>
                      <Label>Armazenamento</Label>
                      <Select value={sample.armazenamento || ''} onValueChange={(value) => updateSampleField(index, 'armazenamento', value)} disabled={!canEdit && Boolean(necropsy)}>
                        <SelectTrigger><SelectValue placeholder="Selecionar armazenamento" /></SelectTrigger>
                        <SelectContent>
                          {sample.armazenamento && !(necropsyCatalogs.storage_options || FALLBACK_STORAGE_OPTIONS).includes(sample.armazenamento) && (
                            <SelectItem value={sample.armazenamento}>{sample.armazenamento}</SelectItem>
                          )}
                          {(necropsyCatalogs.storage_options || FALLBACK_STORAGE_OPTIONS).map((option) => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {sample.armazenamento === 'Outros' && (
                        <Input className="mt-2" value={sample.armazenamento_outros || ''} onChange={(e) => updateSampleField(index, 'armazenamento_outros', e.target.value)} placeholder="Especificar armazenamento" disabled={!canEdit && Boolean(necropsy)} />
                      )}
                    </div>
                    <div>
                      <Label>Modo de preservação</Label>
                      <Select value={sample.modo_preservacao || sample.meio_conservacao || ''} onValueChange={(value) => updateSampleField(index, 'modo_preservacao', value)} disabled={!canEdit && Boolean(necropsy)}>
                        <SelectTrigger><SelectValue placeholder="Selecionar modo" /></SelectTrigger>
                        <SelectContent>
                          {(sample.modo_preservacao || sample.meio_conservacao) && !(necropsyCatalogs.preservation_modes || FALLBACK_PRESERVATION_MODES).includes(sample.modo_preservacao || sample.meio_conservacao) && (
                            <SelectItem value={sample.modo_preservacao || sample.meio_conservacao}>{sample.modo_preservacao || sample.meio_conservacao}</SelectItem>
                          )}
                          {(necropsyCatalogs.preservation_modes || FALLBACK_PRESERVATION_MODES).map((option) => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {sample.modo_preservacao === 'Outros' && (
                        <Input className="mt-2" value={sample.modo_preservacao_outros || ''} onChange={(e) => updateSampleField(index, 'modo_preservacao_outros', e.target.value)} placeholder="Especificar modo" disabled={!canEdit && Boolean(necropsy)} />
                      )}
                    </div>
                    <div>
                      <Label>Destino / setor de análise</Label>
                      <Input value={sample.destino_setor_analise || ''} onChange={(e) => updateSampleField(index, 'destino_setor_analise', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
                    </div>
                    <div>
                      <Label>Laboratório destino</Label>
                      <Input value={sample.laboratorio_destino || ''} onChange={(e) => updateSampleField(index, 'laboratorio_destino', e.target.value)} disabled={!canEdit && Boolean(necropsy)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Observações</Label>
                      <Textarea value={sample.observacoes || ''} onChange={(e) => updateSampleField(index, 'observacoes', e.target.value)} rows={2} disabled={!canEdit && Boolean(necropsy)} />
                    </div>
                  </div>
                  {canEdit && (
                    <div className="mt-3">
                      <Button type="button" variant="ghost" className="text-red-600" onClick={() => removeSample(index)}>
                        Remover amostra
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Registro Fotográfico</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {canAttach && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]">
                  <div>
                    <Label>Tipo de anexo</Label>
                    <Select value={attachmentType} onValueChange={setAttachmentType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ATTACHMENT_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Órgão relacionado</Label>
                    {organCatalog.length > 0 ? (
                      <Select value={attachmentOrganId || '__none__'} onValueChange={(value) => {
                        if (value === '__none__') {
                          setAttachmentOrganId('');
                          setAttachmentOrganName('');
                          return;
                        }
                        const selected = findOrgan(value);
                        setAttachmentOrganId(value);
                        setAttachmentOrganName(selected?.organ_name || selected?.name || '');
                      }}>
                        <SelectTrigger><SelectValue placeholder="Selecionar órgão" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Não vincular</SelectItem>
                          {organCatalog.map((organ) => (
                            <SelectItem key={organ.id} value={organ.id}>{organ.system_name ? `${organ.system_name} - ` : ''}{organ.organ_name || organ.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={attachmentOrganName} onChange={(e) => setAttachmentOrganName(e.target.value)} placeholder="Órgão/material" />
                    )}
                  </div>
                  <div>
                    <Label>Legenda da foto</Label>
                    <Select value={attachmentCaption} onValueChange={setAttachmentCaption}>
                      <SelectTrigger><SelectValue placeholder="Selecionar legenda" /></SelectTrigger>
                      <SelectContent>
                        {photoLegends.map((legend) => (
                          <SelectItem key={legend} value={legend}>{legend}</SelectItem>
                        ))}
                        <SelectItem value="__new__">Cadastrar novo</SelectItem>
                      </SelectContent>
                    </Select>
                    {attachmentCaption === '__new__' && <div className="mt-2 flex gap-2"><Input value={newCaption} onChange={(e) => setNewCaption(e.target.value)} placeholder="Nova legenda" /><Button type="button" onClick={savePhotoLegend} disabled={!newCaption.trim()}>Salvar</Button></div>}
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <Label>Observação</Label>
                    <Input value={attachmentObservation} onChange={(e) => setAttachmentObservation(e.target.value)} placeholder="Observação opcional" />
                  </div>
                  <div className="flex items-end">
                    <CameraFileInput
                      accept="image/*,.pdf,.doc,.docx"
                      multiple
                      disabled={uploading || saving}
                      onFilesSelected={handleAttachmentUpload}
                      fileLabel={uploading ? `Enviando ${uploadCount} arquivo(s)...` : 'Selecionar fotos/anexos'}
                      cameraLabel="Tirar foto"
                    />
                  </div>
                </div>
              )}
              {!necropsy?.id && canAttach && (
                <p className="text-sm text-gray-500">Selecione fotos ou laudos agora. Eles serão enviados automaticamente ao salvar a necropsia.</p>
              )}
              {pendingAttachments.length > 0 && (
                <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-medium text-amber-900">{pendingAttachments.length} anexo(s) aguardando envio</p>
                  {pendingAttachments.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{item.file.name}</p>
                        <p className="text-xs text-gray-600">{item.legenda || item.tipo}{item.orgao_nome ? ` • ${item.orgao_nome}` : ''}</p>
                      </div>
                      <Button type="button" size="sm" variant="outline" disabled={uploading || saving} onClick={() => setPendingAttachments((current) => current.filter((pending) => pending.id !== item.id))}>Remover</Button>
                    </div>
                  ))}
                  {necropsy?.id && <Button type="button" size="sm" variant="outline" disabled={uploading || saving} onClick={() => uploadAttachmentItems(pendingAttachments, necropsy.id)}>Tentar enviar novamente</Button>}
                </div>
              )}
              <div className="space-y-3">
                {(necropsy?.attachments || []).length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum anexo registrado.</p>
                ) : (
                  necropsy.attachments.map((attachment) => (
                    <div key={attachment.id} className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-center gap-3">
                        <ProtectedMediaThumbnail
                          media={attachment}
                          alt={attachment.legenda || attachment.filename || 'Anexo'}
                          className="h-14 w-20"
                          fallbackClassName="h-14 w-20"
                        />
                        <div className="min-w-0">
                        <p className="font-medium text-gray-900">{attachment.legenda || attachment.filename}</p>
                          <p className="text-xs text-gray-500">{attachment.tipo} • {attachment.orgao_nome || 'Órgão não informado'} • {attachment.filename}</p>
                          {attachment.observacao && <p className="text-xs text-gray-600">{attachment.observacao}</p>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`${API}${attachment.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center text-sm font-medium text-emerald-700"
                          onClick={(event) => handleOpenAttachment(event, attachment)}
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Abrir arquivo
                        </a>
                        {canManageAttachment(attachment) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAttachment(attachment)}
                          >
                            <Pencil className="mr-1 h-4 w-4" />
                            Editar
                          </Button>
                        )}
                        {canManageAttachment(attachment) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeleteAttachment(attachment)}
                          >
                            <Trash2 className="mr-1 h-4 w-4" />
                            Excluir
                          </Button>
                        )}
                      </div>
                      {editingAttachment?.file_url === attachment.file_url && (
                        <div className="mt-3 grid gap-3 rounded border border-emerald-100 bg-emerald-50 p-3 sm:col-span-2 md:grid-cols-2">
                          <div>
                            <Label>Legenda fixa</Label>
                            <Select value={attachmentEditForm.legenda} onValueChange={(value) => setAttachmentEditForm((current) => ({ ...current, legenda: value }))}>
                              <SelectTrigger><SelectValue placeholder="Selecionar legenda" /></SelectTrigger>
                              <SelectContent>
                                {attachmentEditForm.legenda && !photoLegends.includes(attachmentEditForm.legenda) && (
                                  <SelectItem value={attachmentEditForm.legenda}>{attachmentEditForm.legenda}</SelectItem>
                                )}
                                {photoLegends.map((legend) => (
                                  <SelectItem key={legend} value={legend}>{legend}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Órgão relacionado</Label>
                            <Select value={attachmentEditForm.orgao_id || '__none__'} onValueChange={(value) => {
                              if (value === '__none__') {
                                setAttachmentEditForm((current) => ({ ...current, orgao_id: '', orgao_nome: '' }));
                                return;
                              }
                              const selected = findOrgan(value);
                              setAttachmentEditForm((current) => ({
                                ...current,
                                orgao_id: value,
                                orgao_nome: selected?.organ_name || selected?.name || ''
                              }));
                            }}>
                              <SelectTrigger><SelectValue placeholder="Selecionar órgão" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Não vincular</SelectItem>
                                {attachmentEditForm.orgao_id && !organCatalog.some((organ) => organ.id === attachmentEditForm.orgao_id) && (
                                  <SelectItem value={attachmentEditForm.orgao_id}>{attachmentEditForm.orgao_nome || attachmentEditForm.orgao_id}</SelectItem>
                                )}
                                {organCatalog.map((organ) => (
                                  <SelectItem key={organ.id} value={organ.id}>{organ.system_name ? `${organ.system_name} - ` : ''}{organ.organ_name || organ.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {attachmentEditForm.orgao_nome && !attachmentEditForm.orgao_id && (
                              <Input className="mt-2" value={attachmentEditForm.orgao_nome} onChange={(e) => setAttachmentEditForm((current) => ({ ...current, orgao_nome: e.target.value }))} />
                            )}
                          </div>
                          <div className="md:col-span-2">
                            <Label>Observação</Label>
                            <Input value={attachmentEditForm.observacao} onChange={(e) => setAttachmentEditForm((current) => ({ ...current, observacao: e.target.value }))} />
                          </div>
                          <div className="flex flex-wrap gap-2 md:col-span-2">
                            <Button type="button" size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveAttachmentEdit}>
                              Salvar edição
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => setEditingAttachment(null)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {(form.projeto_vinculado || form.pendencias_projeto || form.observacoes_finais) && <div className="rounded border bg-gray-50 p-3 text-sm text-gray-600">
            <strong>Informações legadas</strong>
            {form.projeto_vinculado && <p>Projeto vinculado: {form.projeto_vinculado}</p>}
            {form.pendencias_projeto && <p>Pendências do projeto: {form.pendencias_projeto}</p>}
            {form.observacoes_finais && <p>Observações finais: {form.observacoes_finais}</p>}
          </div>}

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {(canCreate || canEdit) && (
              <Button onClick={handleSave} disabled={saving || uploading} className="bg-orange-600 hover:bg-orange-700">
                {saving ? 'Salvando...' : 'Salvar como rascunho'}
              </Button>
            )}
            {canConclude && necropsy?.id && (
              <>
                <Select value={conclusionStatus} onValueChange={setConclusionStatus}>
                  <SelectTrigger className="w-full sm:w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.filter((option) => option.value !== 'necropsia_iniciada').map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={handleConclude} disabled={saving || uploading || pendingAttachments.length > 0}>
                  Concluir Necropsia
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
